import React from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import DownloadIcon from '@mui/icons-material/Download';
import Button from '@mui/material/Button';
import ReactGA from 'react-ga4';
import logo from '../assets/images/finaculator1.png';
import addWatermark from './watermark';
import { useState } from "react";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';


interface LoanPDFGeneratorProps {
  loan1EMI: number;
  loan1TotalInterest: number;
  loan1FinalAmount: number;
  loan2EMI: number;
  loan2TotalInterest: number;
  loan2FinalAmount: number;
  loan1Amount: string;
  loan1Rate: string;
  loan1Period: string;
  loan2Amount: string;
  loan2Rate: string;
  loan2Period: string;
  isLoan1InMonths: boolean;
  isLoan2InMonths: boolean;
  isCalculated?:boolean,

  formatNumberWithCommas: (value: number | string) => string;
}

const LoanPDFGenerator: React.FC<LoanPDFGeneratorProps> = ({
  loan1EMI,
  loan1TotalInterest,
  loan1FinalAmount,
  loan2EMI,
  loan2TotalInterest,
  loan2FinalAmount,
  loan1Amount,
  loan1Rate,
  loan1Period,
  loan2Amount,
  loan2Rate,
  loan2Period,
  isLoan1InMonths,
  isLoan2InMonths,
  isCalculated,
  formatNumberWithCommas,
}) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const generatePDF = () => {
    ReactGA.event({
      category: 'Loan Comparison PDF',
      action: 'Loan Comparison PDF Button',
      label: 'Loan Comparison PDF'
    });

    if (!isCalculated) {
      setSnackbarMessage("Please calculate the values before downloading the PDF.");
      setSnackbarOpen(true);
      return;
    }
   

    if (!loan1Amount ||
      !loan1Rate ||
      !loan1Period ||
      !loan2Amount ||
      !loan2Rate ||
      !loan2Period ||
      !loan1EMI ||
      !loan1TotalInterest ||
      !loan1FinalAmount ||
      !loan2EMI ||
      !loan2TotalInterest ||
      !loan2FinalAmount ||
      !formatNumberWithCommas ||
      !isCalculated) {
      setSnackbarMessage("Please ensure all fields are filled before downloading the PDF.");
      setSnackbarOpen(true);
      return;
    }

    const doc = new jsPDF();
    const watermarkWidth = 100;
    const watermarkHeight = 100;

    doc.text('Loan Comparison Summary', 14, 20);

    const userInputData = [
      {
        label: 'Loan 1 Amount',
        value: `${formatNumberWithCommas(parseFloat(loan1Amount.replace(/,/g, "")) || 0)}`,
      },
      {
        label: 'Loan 1 Rate',
        value: `${loan1Rate}%`,
      },
      {
        label: 'Loan 1 Period',
        value: `${loan1Period} ${isLoan1InMonths ? 'months' : 'years'}`,
      },
      {
        label: 'Loan 2 Amount',
        value: `${formatNumberWithCommas(parseFloat(loan2Amount.replace(/,/g, "")) || 0)}`,
      },
      {
        label: 'Loan 2 Rate',
        value: `${loan2Rate}%`,
      },
      {
        label: 'Loan 2 Period',
        value: `${loan2Period} ${isLoan2InMonths ? 'months' : 'years'}`,
      },
    ];

    autoTable(doc, {
      head: [['Detail', 'Value']],
      body: userInputData.map(row => [row.label, row.value]),
      startY: 30,
      theme: 'grid',
      styles: {
        fontSize: 10,
        cellPadding: 5,
        overflow: 'linebreak',
        halign: 'left',
        valign: 'middle',
      },
      headStyles: {
        fillColor: [22, 160, 133],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [240, 240, 240],
      },
    });

    const comparisonData = [
      {
        type: 'Loan 1',
        emi: `${formatNumberWithCommas(loan1EMI.toFixed(2))}`,
        interest: `${formatNumberWithCommas(loan1TotalInterest.toFixed(2))}`,
        amount: `${formatNumberWithCommas(loan1FinalAmount.toFixed(2))}`,
      },
      {
        type: 'Loan 2',
        emi: `${formatNumberWithCommas(loan2EMI.toFixed(2))}`,
        interest: `${formatNumberWithCommas(loan2TotalInterest.toFixed(2))}`,
        amount: `${formatNumberWithCommas(loan2FinalAmount.toFixed(2))}`,
      },
    ];

    autoTable(doc, {
      head: [['Loan Type', 'Monthly EMI', 'Total Interest', 'Final Amount']],
      body: comparisonData.map(row => [row.type, row.emi, row.interest, row.amount]),
      startY: doc.autoTable.previous.finalY + 10,
      theme: 'grid',
      styles: {
        fontSize: 10,
        cellPadding: 5,
        overflow: 'linebreak',
        halign: 'center',
        valign: 'middle',
      },
      headStyles: {
        fillColor: [22, 160, 133],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [240, 240, 240],
      },
    });

    addWatermark(doc, logo, watermarkWidth, watermarkHeight);

    const img = new Image();
    img.src = logo;
    img.onload = () => {
      const now = new Date();
      const padZero = (num: string | number) => ("0" + num).slice(-2);
      const formattedDate = `${now.getFullYear()}-${padZero(now.getMonth() + 1)}-${padZero(now.getDate())}`;
      const formattedTime = `${padZero(now.getHours())}-${padZero(now.getMinutes())}-${padZero(now.getSeconds())}`;
      const timestamp = `${formattedDate}_${formattedTime}`;
      const fileName = `finaculator_loan_comparison_calculator_${timestamp}.pdf`;
      doc.save(fileName);
    };
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
      <Button
        variant="contained"
        color="success"
        onClick={generatePDF}
        style={{ marginTop: -70, marginRight: 90 }}
        startIcon={<DownloadIcon />}
      >
        Download PDF
      </Button>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} 
      >
        <MuiAlert onClose={handleSnackbarClose} severity="warning" sx={{ width: '100%' }}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default LoanPDFGenerator;
